import { createElement } from "react"

import isEmpty from "lodash-es/isEmpty"
import isString from "lodash-es/isString"

import { EButtonSize, EButtonVariant, type TButtonProps } from "@/3514/components"
import {
    EAttendanceModalOrigin,
    type TAttendanceModalProps,
    attendanceMarksQueryPrefetcher,
    isEnoughTimeToMarkAttendance
} from "@/3514/modals/attendance"
import { EModalId } from "@/3514/store/modal"
import { THEME } from "@/3514/theme"
import { withPressHandler } from "@/3514/utils"
import { type TEmptyAsyncCallback } from "@/shared/types/functions"

import assets from "./session-info.assets"
import type {
    _TSessionInfoRenderFunctionSharedExternalParams as TRenderFunctionExternalParams,
    _TSessionInfoRenderButtonPropsFunctionInternalParams as TRenderFunctionInternalParams
} from "./session-info.types"

function renderJoinButtonProps(params: TRenderFunctionInternalParams & TRenderFunctionExternalParams): TButtonProps {
    const _actionHandler: ReturnType<typeof withPressHandler> = withPressHandler(
        (): void => void window.open(params.data.session.joinUrl, "_blank", "noreferrer")
    )

    return {
        variant: EButtonVariant.Solid,
        isDisabled: Boolean(params.isAccordionExpanded && params.isAccordionExpandable)
            ? Boolean(!isString(params.data.session.joinUrl || isEmpty(params.data.session.joinUrl))) ||
              Boolean(!params.sessionTimeState.isLive || !params.sessionTimeState.isUpcoming)
            : true,
        children: [createElement(assets.ClockIcon), params.t("common.joinSession")],
        onClick: _actionHandler,
        onKeyDown: _actionHandler,
        minWidth: "130px",
        size: EButtonSize.Small,
        className: "max-w-fit w-fit gap-x-[10px]"
    }
}

function renderAttendanceButtonProps(
    params: TRenderFunctionInternalParams & TRenderFunctionExternalParams
): TButtonProps {
    const isAppropriateTime: boolean = isEnoughTimeToMarkAttendance(
        params.data.session.startDate,
        params.data.session.minHoursToEdit
    )

    const _actionHandler: ReturnType<typeof withPressHandler> = withPressHandler((): void =>
        params.modalDispatcher.showModal<TAttendanceModalProps>(EModalId.Attendance, {
            origin: EAttendanceModalOrigin.CoachingSessions,
            sessionId: params.data.session.id,
            sessionModality: params.data.session.modality,
            sessionStartTime: params.data.session.startDate,
            minHoursToMark: params.data.session.minHoursToEdit,
            groupId: params.data.session.groupId,
            participantId: params.data.user.id
        })
    )

    const _focusHandler: TEmptyAsyncCallback = async (): Promise<void> => {
        if (params.isNetworkFast && isAppropriateTime)
            await attendanceMarksQueryPrefetcher({ qc: params.qc }, { sessionId: params.data.session.id })
    }

    return {
        variant: EButtonVariant.Outlined,
        isDisabled: !params.isAccordionExpanded || !isAppropriateTime,
        isButtonAccessibleOnDisabled: true,
        children: [
            createElement(assets.AttendanceIcon, {
                fillColor: isAppropriateTime ? THEME.COLORS.blue[200] : THEME.COLORS.gray[300]
            }),
            params.t("common.attendance")
        ],
        onClick: _actionHandler,
        onKeyDown: _actionHandler,
        onFocus: _focusHandler,
        onMouseEnter: _focusHandler,
        minWidth: "125px",
        size: EButtonSize.Small,
        className: "max-w-fit w-fit gap-x-[10px]"
    }
}

export default {
    renderJoinButtonProps,
    renderAttendanceButtonProps
}
